import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertTriangle, MessageSquare } from 'lucide-react';
import { Loader } from '../../components/ui';
import { supabaseService } from '../../lib/supabase';
import { logger } from '../../utils/logger';

interface JobStatus {
  id: string;
  status: string;
  timestamp: string;
  processed: number;
  successful: number;
  regenerated?: boolean;
  totalMessages?: number;
  inProgress?: boolean;
}

export function ChatMessageSummaryBatchJob() {
  const [messagesNeedingSummaries, setMessagesNeedingSummaries] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [recentJobs, setRecentJobs] = useState<JobStatus[]>([]);
  // Uncommenting these when implementing the regenerate mode toggle
  // const [regenerateMode, setRegenerateMode] = useState<boolean>(false);
  const [batchSize, setBatchSize] = useState<number>(20);

  // Load count of messages needing summaries
  useEffect(() => {
    loadMessagesCount();

    // Cleanup polling interval on unmount (no polling implemented yet)
  }, []);

  // Function to query the database for messages without summaries
  const loadMessagesCount = async () => {
    setIsLoading(true);
    try {
      const supabase = await supabaseService.getClient();
      // Count AI assistant messages without summaries
      const query = supabase
        .from('ai_chat_messages')
        .select('id', { count: 'exact', head: true })
        .eq('role', 'assistant');

      // Only filter for null summaries if not checking for all messages
      const { count, error } = await query.is('summary', null);

      if (error) throw error;

      setMessagesNeedingSummaries(count || 0);
      logger.component('Loaded count of messages needing summaries', { count });
      setError(null);
    } catch (error) {
      logger.error('Error loading messages count:', error);
      setError(error instanceof Error ? error.message : 'Failed to load messages count');
    } finally {
      setIsLoading(false);
    }
  };

  // Function to process a single batch within a job
  const processBatch = async (
    jobId: string,
    regenerateExisting: boolean,
    totalMessages: number,
    processedIds: string[] = []
  ) => {
    try {
      // Call API to process a single small batch
      const response = await fetch('/.netlify/functions/chat-message-summary', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          batchSize: 5, // Use a small safe batch size
          regenerateExisting,
          totalMessages,
          processedIds,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to generate summaries: ${errorText}`);
      }

      // Get response
      const result = await response.json();

      if (result.success) {
        // Update job status with latest values
        setRecentJobs(prev =>
          prev.map(job =>
            job.id === jobId
              ? {
                  ...job,
                  processed: (job.processed || 0) + (result.processed || 0),
                  successful: (job.successful || 0) + (result.successful || 0),
                  // Update total count if needed (might be more accurate now)
                  totalMessages: Math.max(
                    job.totalMessages || 0,
                    (job.processed || 0) + (result.processed || 0)
                  ),
                  inProgress: result.hasMore, // Keep running if more messages to process
                }
              : job
          )
        );

        // If more messages to process, continue with next batch
        if (result.hasMore) {
          // Continue with the next batch using the updated processed IDs
          setTimeout(() => {
            processBatch(jobId, regenerateExisting, totalMessages, result.processedIds);
          }, 1000); // Add a short delay to prevent overlapping requests
        } else {
          // Job is complete
          setRecentJobs(prev =>
            prev.map(job =>
              job.id === jobId
                ? {
                    ...job,
                    status: 'completed',
                    inProgress: false,
                  }
                : job
            )
          );

          // Refresh to see updated status and reset processing state
          await loadMessagesCount();
          setIsProcessing(false);
        }
      } else {
        throw new Error(result.error || 'Unknown error occurred');
      }
    } catch (error) {
      // Mark job as failed
      setRecentJobs(prev =>
        prev.map(job =>
          job.id === jobId
            ? {
                ...job,
                status: 'failed',
                inProgress: false,
              }
            : job
        )
      );

      logger.error('Error processing batch:', error);
      setError(error instanceof Error ? error.message : 'Failed to process message batch');
      setIsProcessing(false);

      // Also update the total count for this job based on what we've processed so far
      setRecentJobs(prev =>
        prev.map(job =>
          job.id === jobId
            ? {
                ...job,
                // Make sure totalMessages is at least as large as what we've processed
                totalMessages: Math.max(job.totalMessages || 0, job.processed || 0),
              }
            : job
        )
      );
    }
  };

  // Function to trigger the summary generation process
  const startJob = async (regenerateExisting = false) => {
    try {
      setIsLoading(true);
      setIsProcessing(true);
      setError(null);

      const supabase = await supabaseService.getClient();

      // Count total messages that will be processed to track progress
      let totalMessagesToProcess = 0;
      try {
        // Build the query for total count
        let countQuery = supabase
          .from('ai_chat_messages')
          .select('id', { count: 'exact', head: true })
          .eq('role', 'assistant');

        // If not regenerating, only count those without summaries
        if (!regenerateExisting) {
          countQuery = countQuery.is('summary', null);
        }

        const { count, error } = await countQuery;

        if (!error) {
          totalMessagesToProcess = count || 0;
          console.log(`Will process ${totalMessagesToProcess} messages total`);

          // If we got 0 but we're regenerating, get the total number of assistant messages
          if (regenerateExisting && totalMessagesToProcess === 0) {
            const { count: totalCount } = await supabase
              .from('ai_chat_messages')
              .select('id', { count: 'exact', head: true })
              .eq('role', 'assistant');

            if (totalCount) {
              totalMessagesToProcess = totalCount;
              console.log(
                `Updated count for regeneration: ${totalMessagesToProcess} total AI messages`
              );
            }
          }
        }
      } catch (countError) {
        console.error('Error counting total messages:', countError);
      }

      // Add a job to the recent jobs list
      const jobId = Date.now().toString();
      const newJob = {
        id: jobId,
        status: 'processing',
        timestamp: new Date().toISOString(),
        processed: 0,
        successful: 0,
        regenerated: regenerateExisting,
        totalMessages: totalMessagesToProcess,
        inProgress: true,
      };

      setRecentJobs(prev => [newJob, ...prev].slice(0, 5)); // Keep at most 5 jobs

      // Start the batch processing
      processBatch(jobId, regenerateExisting, totalMessagesToProcess, []);

      // We don't need to wait for completion as each batch will update the job status
      setIsLoading(false);
    } catch (error) {
      logger.error('Error starting summary generation job:', error);
      setError(error instanceof Error ? error.message : 'Failed to start summary generation');
      setIsLoading(false);
      setIsProcessing(false);
    }
  };

  return (
    <div className="bg-gray-800 rounded-lg shadow-2xl p-6 mb-8 border border-gray-700">
      <h2 className="text-xl font-semibold mb-4 text-white">AI Chat Message Summary Generator</h2>

      {isLoading && !error ? (
        <div className="flex items-center justify-center py-4 text-white">
          <Loader size="small" color="text-blue-400" className="mr-2" />
          <span>Loading...</span>
        </div>
      ) : error ? (
        <div className="bg-red-900/20 border border-red-500 rounded-md p-4 mb-4 text-red-400">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 mr-2" />
            <span>{error}</span>
          </div>
        </div>
      ) : (
        <>
          <div className="mb-6 bg-gray-900/50 border border-gray-600 rounded-md p-4">
            <h3 className="text-blue-300 font-medium text-sm mb-2">
              About AI Chat Message Summaries
            </h3>
            <p className="text-gray-300 text-sm mb-3">
              This feature uses AI to automatically generate concise summaries for chat messages
              from the AI assistant. These summaries are displayed when messages are collapsed,
              making conversations easier to navigate.
            </p>
            <p className="text-gray-300 text-sm mb-3">
              Only AI assistant messages are summarized. To avoid timeouts, messages are processed
              in small batches until completion, with progress tracking. You can safely navigate
              away and return to check status. The migration must be applied first to add the
              summary column.
            </p>
            <p className="text-gray-300 text-sm">
              <strong>Current status:</strong>{' '}
              {messagesNeedingSummaries !== null
                ? `${messagesNeedingSummaries} messages`
                : 'Unknown number of messages'}{' '}
              waiting for AI summaries.
            </p>
          </div>

          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-300">Batch Size</h3>
              <div className="flex items-center">
                <input
                  id="batchSize"
                  type="number"
                  min="1"
                  max="30"
                  value={batchSize}
                  onChange={e =>
                    setBatchSize(Math.max(1, Math.min(30, parseInt(e.target.value) || 10)))
                  }
                  className="border border-gray-700 rounded bg-gray-900 text-white px-3 py-1 mr-2 w-20 text-right focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  disabled={isProcessing}
                />
                <span className="text-xs text-gray-400">messages</span>
              </div>
            </div>
            <p className="text-xs text-gray-400 mb-4">
              Smaller batches (10-20) complete faster but require more jobs. Larger batches may time
              out.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <button
              onClick={() => startJob(false)}
              disabled={isProcessing || messagesNeedingSummaries === 0}
              className="bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-md transition disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm flex items-center justify-center"
            >
              {isProcessing ? (
                <>
                  <Loader size="small" className="mr-2" />
                  Processing...
                </>
              ) : (
                <>
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Process Missing Summaries
                </>
              )}
            </button>

            <button
              onClick={() => startJob(true)}
              disabled={isProcessing}
              className="bg-purple-600 hover:bg-purple-700 text-white py-3 px-6 rounded-md transition disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
            >
              Regenerate All Summaries (Batch Process)
            </button>

            <button
              onClick={loadMessagesCount}
              disabled={isProcessing}
              className="bg-gray-700 hover:bg-gray-600 text-white py-3 px-6 rounded-md transition disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
            >
              Refresh Count
            </button>
          </div>

          {recentJobs.length > 0 && (
            <div>
              <h3 className="text-lg font-medium mb-3 text-white">Recent Jobs</h3>
              <div className="border border-gray-700 rounded-md overflow-hidden">
                <table className="min-w-full divide-y divide-gray-700">
                  <thead className="bg-gray-900">
                    <tr>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                      >
                        ID
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                      >
                        Status
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                      >
                        Started
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                      >
                        Progress
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-gray-800 divide-y divide-gray-700">
                    {recentJobs.map(job => (
                      <tr key={job.id} className="hover:bg-gray-700 transition-colors">
                        <td className="px-4 py-3 text-sm text-gray-300">{job.id.slice(-6)}</td>
                        <td className="px-4 py-3 text-sm">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              job.status === 'completed'
                                ? 'bg-green-900/20 text-green-400'
                                : job.status === 'failed'
                                  ? 'bg-red-900/20 text-red-400'
                                  : job.status === 'processing'
                                    ? 'bg-blue-900/20 text-blue-400'
                                    : job.status === 'timeout'
                                      ? 'bg-yellow-900/20 text-yellow-400'
                                      : 'bg-gray-900/20 text-gray-400'
                            }`}
                          >
                            {job.status === 'completed' && <CheckCircle className="w-3 h-3 mr-1" />}
                            {job.status === 'timeout' ? 'Timed Out' : job.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-400">
                          {new Date(job.timestamp).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-400">
                          {job.processed > 0 ? (
                            <div className="space-y-2">
                              <div className="flex justify-between items-center">
                                <span>
                                  {job.successful} / {job.processed} successful
                                  {job.totalMessages && (
                                    <span className="ml-2">of {job.totalMessages} total</span>
                                  )}
                                </span>
                                {job.regenerated && (
                                  <span className="ml-2 text-xs px-2 py-0.5 bg-purple-900/20 text-purple-400 rounded-full">
                                    Regenerated
                                  </span>
                                )}
                              </div>

                              {job.totalMessages && job.totalMessages > 0 && (
                                <div className="w-full bg-gray-700 rounded-full h-1.5 overflow-hidden">
                                  <div
                                    className="bg-blue-500 h-1.5 rounded-full"
                                    style={{
                                      width: `${Math.min(100, Math.round((job.processed / job.totalMessages) * 100))}%`,
                                    }}
                                  ></div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-500">Not started</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
